import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  LockOutlined as LockIcon,
  AccessTime as TimeIcon,
  Launch as LaunchIcon,
  ArrowBack as BackIcon,
  PersonOutline as PersonIcon,
  FileCopy as CopyIcon,
  FileDownload as ImportIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { PasswordVerifyDialog } from '../components/share/PasswordProtection';
import { ShareType } from '../types/shares';
import { formatFileSize } from '../utils/formatters';
import usePageTitle from '../hooks/usePageTitle';
import PreviewEngine from '../preview/PreviewEngine';
import { RootState } from '../store';
import config from '../config';

import { getDriveApiToken } from '../lib/auth-token';
const apiBaseUrl = (process.env.REACT_APP_API_URL || config.server.baseUrl || '/api').replace(
  /\/$/,
  ''
);

const api = axios.create({
  baseURL: apiBaseUrl,
});

api.interceptors.request.use((cfg) => {
  const token = getDriveApiToken();
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

const sharePasswordKey = (shareToken: string) => `share_pwd_${shareToken}`;

interface SharedItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimeType?: string;
  size?: number;
  createdAt: string;
  updatedAt: string;
  sharedBy?: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
  } | null;
  shareInfo: {
    id: string;
    token: string;
    shareType: ShareType;
    accessMode?: 'private' | 'public';
    publicAccessType?: string;
    expiresAt?: string;
    isPasswordProtected: boolean;
    maxViews?: number | null;
    viewsRemaining?: number | null;
    lastAccessedAt?: string;
    accessCount: number;
  };
  path?: string[];
  children?: {
    folders: SharedFolder[];
    files: SharedFile[];
  };
}

interface SharedFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl?: string;
}

interface SharedFolder {
  id: string;
  name: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

const SharedItemViewer: React.FC = () => {
  usePageTitle('Shared Item');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isLoggedIn = useSelector(
    (state: RootState) => state.auth.isAuthenticated && Boolean(state.auth.token)
  );
  
  const [sharedItem, setSharedItem] = useState<SharedItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [isPasswordDialog, setIsPasswordDialog] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [viewLimitReached, setViewLimitReached] = useState(false);
  const [sharePassword, setSharePassword] = useState<string | null>(null);
  
  // Current view - relevant for folder navigation
  const [currentView, setCurrentView] = useState<{
    folders: SharedFolder[];
    files: SharedFile[];
  }>({
    folders: [],
    files: [],
  });

  const fetchAbortRef = useRef<AbortController | null>(null);

  const buildSharePreviewUrl = useCallback(
    (fileId: string, password?: string | null) => {
      const params = new URLSearchParams({ inline: '1' });
      if (password) params.set('password', password);
      return `${apiBaseUrl}/shares/public/${token}/download/${fileId}?${params.toString()}`;
    },
    [token]
  );

  const fetchSharedItem = async (
    shareToken: string,
    folderPath: string = '',
    passwordOverride?: string
  ) => {
    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    try {
      setLoading(true);
      setError(null);
      setNeedsLogin(false);
      setViewLimitReached(false);

      const pwd = passwordOverride ?? sharePassword ?? undefined;
      const params: Record<string, string> = {};
      if (folderPath) params.path = folderPath;
      if (pwd) params.password = pwd;

      const response = await api.get(`/shares/public/${shareToken}`, {
        params,
        signal: abortController.signal,
      });
      const data = response.data;
      
      setSharedItem(data);
      
      // Set the current view (files and folders)
      if (data.type === 'folder') {
        setCurrentView({
          folders: data.children?.folders || [],
          files: data.children?.files || [],
        });
        
        // Update path if provided (ensure it's always an array)
        if (data.path && Array.isArray(data.path)) {
          setCurrentPath(data.path);
        } else {
          setCurrentPath([]);
        }
      } else {
        // For files, clear the path
        setCurrentPath([]);
      }
      
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      const data = err.response?.data;
      if (
        err.response?.status === 401 &&
        (data?.requirePassword || data?.requiresPassword)
      ) {
        setNeedsPassword(true);
        setIsPasswordDialog(true);
      } else if (err.response?.status === 403 && data?.requireLogin) {
        setNeedsLogin(true);
        setError(data.message || 'Sign in with your organization account to view this item');
      } else if (err.response?.status === 403 && data?.viewLimitExceeded) {
        setViewLimitReached(true);
        setError(data.message || 'This link has reached its view limit');
      } else {
        setError(data?.message || 'Error loading shared item');
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Initial data loading
  useEffect(() => {
    if (!token) {
      setError('Invalid share link');
      setLoading(false);
      return;
    }

    const storedPwd = sessionStorage.getItem(sharePasswordKey(token));
    if (storedPwd) {
      setSharePassword(storedPwd);
    }
    fetchSharedItem(token, '', storedPwd || undefined);

    return () => {
      fetchAbortRef.current?.abort();
    };
  }, [token]);

  // Navigate into a subfolder
  const handleFolderClick = (folderId: string, folderName: string) => {
    const newPath = [...currentPath, folderName];
    setCurrentPath(newPath);
    
    // Convert path to URL-friendly format
    const pathQuery = newPath.join('/');
    fetchSharedItem(token!, pathQuery, sharePassword || undefined);
  };

  const handleBackClick = () => {
    if (currentPath.length === 0) return;
    
    const newPath = [...currentPath];
    newPath.pop();
    setCurrentPath(newPath);
    
    const pathQuery = newPath.join('/');
    fetchSharedItem(token!, pathQuery, sharePassword || undefined);
  };

  // Download a file
  const handleFileDownload = async (fileId: string, fileName: string) => {
    try {
      enqueueSnackbar('Starting download...', { variant: 'info' });
      
      const response = await api.get(`/shares/public/${token}/download/${fileId}`, {
        responseType: 'blob',
        params: sharePassword ? { password: sharePassword } : undefined,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      enqueueSnackbar('Download complete', { variant: 'success' });
    } catch (err) {

      enqueueSnackbar('Download failed', { variant: 'error' });
    }
  };

  // Download entire folder as zip
  const handleFolderDownload = async () => {
    try {
      if (!sharedItem) return;
      
      enqueueSnackbar('Preparing folder for download...', { variant: 'info' });
      
      const response = await api.get(`/shares/public/${token}/download-folder`, {
        params: {
          path: currentPath.join('/'),
          ...(sharePassword ? { password: sharePassword } : {}),
        },
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sharedItem.name}.zip`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      enqueueSnackbar('Download complete', { variant: 'success' });
    } catch (err) {

      enqueueSnackbar('Download failed', { variant: 'error' });
    }
  };

  // Import to your drive
  const handleImportToDrive = async () => {
    try {
      if (!sharedItem) return;
      if (!isLoggedIn) {
        enqueueSnackbar('Sign in to import this item to your Drive', { variant: 'info' });
        await handleSignInForPrivateShare();
        return;
      }

      enqueueSnackbar('Importing to your drive...', { variant: 'info' });
      
      // Import request
      await api.post(`/shares/public/${token}/import`, {
        path: currentPath.join('/'),
        ...(sharePassword ? { password: sharePassword } : {}),
      });
      
      enqueueSnackbar('Successfully imported to your drive', { variant: 'success' });
      
      // Optionally navigate to the imported item
      navigate('/files');
    } catch (err) {

      enqueueSnackbar('Import failed. Please try again or log in.', { variant: 'error' });
    }
  };

  // Password verification callback
  const handlePasswordVerified = (password: string) => {
    setSharePassword(password);
    sessionStorage.setItem(sharePasswordKey(token!), password);
    setIsPasswordDialog(false);
    setNeedsPassword(false);
    fetchSharedItem(token!, currentPath.join('/'), password);
  };

  const handleSignInForPrivateShare = async () => {
    localStorage.setItem('share_return_url', window.location.pathname + window.location.search);
    const { beginDriveReauth } = await import('../lib/auth-token');
    beginDriveReauth({
      reason: 'MISSING_TOKEN',
      returnUrl: window.location.href
    });
  };

  // Copy share link to clipboard
  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(
      () => {
        enqueueSnackbar('Share link copied to clipboard', { variant: 'success' });
      },
      (err) => {

        enqueueSnackbar('Failed to copy link', { variant: 'error' });
      }
    );
  };

  // Render file icon based on mimetype
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    }
    if (mimeType === 'application/pdf') {
      return <PdfIcon color="error" />;
    }
    if (mimeType.startsWith('video/')) {
      return <VideoIcon color="secondary" />;
    }
    if (mimeType.startsWith('audio/')) {
      return <AudioIcon color="success" />;
    }
    return <FileIcon color="info" />;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading shared content...
        </Typography>
      </Container>
    );
  }

  if (error && !needsPassword) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity={viewLimitReached || needsLogin ? 'warning' : 'error'} variant="filled">
          <AlertTitle>
            {viewLimitReached
              ? 'View limit reached'
              : needsLogin
                ? 'Sign in required'
                : 'Error'}
          </AlertTitle>
          {error}
        </Alert>
        <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
          {needsLogin && (
            <Button variant="contained" onClick={handleSignInForPrivateShare}>
              Sign in to continue
            </Button>
          )}
          <Button variant={needsLogin ? 'outlined' : 'contained'} onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </Box>
      </Container>
    );
  }

  if (!sharedItem && !needsPassword) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning" variant="filled">
          <AlertTitle>Item Not Found</AlertTitle>
          The shared item you're looking for doesn't exist or has been removed.
        </Alert>
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        minHeight: '100vh',
        py: 4,
        bgcolor: 'background.default',
      }}
    >
    <Container maxWidth="lg">
      {needsPassword && (
        <PasswordVerifyDialog
          open={isPasswordDialog}
          onClose={() => navigate('/')} // If user cancels, go to home
          onPasswordVerified={handlePasswordVerified}
          shareToken={token!}
        />
      )}
      
      {sharedItem && (
        <>
          {/* Header with information */}
          <Paper 
            elevation={0} 
            variant="outlined" 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: theme.shape.borderRadius,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'absolute', top: 0, right: 0, p: 1.5, display: 'flex', gap: 0.5 }}>
              <Chip
                label={
                  sharedItem.shareInfo.accessMode === 'private'
                    ? 'Organization only'
                    : 'Public link'
                }
                color={sharedItem.shareInfo.accessMode === 'private' ? 'warning' : 'success'}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={sharedItem.shareInfo.shareType === 'view' ? 'View Only' : 'Can Edit'} 
                color={sharedItem.shareInfo.shareType === 'view' ? 'primary' : 'secondary'} 
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {sharedItem.type === 'folder' ? (
                <FolderIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
              ) : (
                getFileIcon(sharedItem.mimeType || 'application/octet-stream')
              )}
              
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 500 }}>
                  {sharedItem.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sharedItem.sharedBy
                    ? `Shared by ${sharedItem.sharedBy.name} (${sharedItem.sharedBy.email})`
                    : 'Shared item'}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {sharedItem.sharedBy && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Owner:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <Avatar 
                      src={sharedItem.sharedBy.avatarUrl}
                      sx={{ width: 24, height: 24, mr: 1 }}
                    >
                      {sharedItem.sharedBy.name?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2">{sharedItem.sharedBy.name}</Typography>
                  </Box>
                </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Modified:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {format(new Date(sharedItem.updatedAt), 'PPP')}
                  </Typography>
                </Box>
                
                {sharedItem.type === 'file' && sharedItem.size !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FileIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Size:
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {formatFileSize(sharedItem.size)}
                    </Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} sm={6}>
                {sharedItem.shareInfo.expiresAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expires:
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {format(new Date(sharedItem.shareInfo.expiresAt), 'PPP')}
                    </Typography>
                  </Box>
                )}
                
                {sharedItem.shareInfo.isPasswordProtected && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LockIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Protected by password
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LaunchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Accesses:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {sharedItem.shareInfo.accessCount} times
                    {sharedItem.shareInfo.maxViews != null
                      ? ` (max ${sharedItem.shareInfo.maxViews})`
                      : ''}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {sharedItem.type === 'file' ? (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleFileDownload(sharedItem.id, sharedItem.name)}
                >
                  Download
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleFolderDownload}
                >
                  Download as ZIP
                </Button>
              )}
              
              {isLoggedIn && (
                <Button
                  variant="outlined"
                  startIcon={<ImportIcon />}
                  onClick={handleImportToDrive}
                >
                  Import to My Drive
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyLink}
              >
                Copy Link
              </Button>
            </Box>
          </Paper>
          
          {/* Content preview / browser */}
          {sharedItem.type === 'folder' && (
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 2, 
                borderRadius: theme.shape.borderRadius 
              }}
            >
              {/* Breadcrumbs for folder navigation */}
              {currentPath.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Breadcrumbs separator="›" aria-label="folder navigation breadcrumb">
                    <Link
                      sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      color="inherit"
                      onClick={() => {
                        setCurrentPath([]);
                        fetchSharedItem(token!);
                      }}
                    >
                      <FolderIcon fontSize="small" sx={{ mr: 0.5 }} />
                      {sharedItem.name}
                    </Link>
                    
                    {currentPath.map((folder, index) => {
                      // Skip the last one since it's the current folder
                      if (index === currentPath.length - 1) {
                        return (
                          <Typography key={index} color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <FolderIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {folder}
                          </Typography>
                        );
                      }
                      
                      // For intermediate folders, allow navigation
                      const pathToHere = currentPath.slice(0, index + 1);
                      return (
                        <Link
                          key={index}
                          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          color="inherit"
                          onClick={() => {
                            setCurrentPath(pathToHere);
                            fetchSharedItem(token!, pathToHere.join('/'));
                          }}
                        >
                          <FolderIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {folder}
                        </Link>
                      );
                    })}
                  </Breadcrumbs>
                </Box>
              )}
              
              {/* Back button - only when inside folders */}
              {currentPath.length > 0 && (
                <Button
                  startIcon={<BackIcon />}
                  onClick={handleBackClick}
                  variant="outlined"
                  sx={{ mb: 2 }}
                >
                  Back
                </Button>
              )}
              
              {/* Folders */}
              {currentView.folders.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                    Folders
                  </Typography>
                  
                  <List disablePadding>
                    {currentView.folders.map((folder) => (
                      <Paper
                        key={folder.id}
                        variant="outlined"
                        sx={{ 
                          mb: 1, 
                          borderRadius: theme.shape.borderRadius,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleFolderClick(folder.id, folder.name)}
                        >
                          <ListItemIcon>
                            <FolderIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={folder.name}
                            secondary={`${folder.itemCount} items • Modified ${format(new Date(folder.updatedAt), 'PPP')}`}
                          />
                        </ListItemButton>
                      </Paper>
                    ))}
                  </List>
                </>
              )}
              
              {/* Files */}
              {currentView.files.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Files
                  </Typography>
                  
                  <List disablePadding>
                    {currentView.files.map((file) => (
                      <Paper
                        key={file.id}
                        variant="outlined"
                        sx={{ 
                          mb: 1, 
                          borderRadius: theme.shape.borderRadius,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        <ListItem
                          secondaryAction={
                            <Tooltip title="Download file">
                              <IconButton 
                                edge="end" 
                                onClick={() => handleFileDownload(file.id, file.name)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemIcon>
                            {getFileIcon(file.mimeType)}
                          </ListItemIcon>
                          <ListItemText
                            primary={file.name}
                            secondary={`${formatFileSize(file.size)} • Modified ${format(new Date(file.updatedAt), 'PPP')}`}
                          />
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                </>
              )}
              
              {currentView.folders.length === 0 && currentView.files.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6">This folder is empty</Typography>
                  <Typography variant="body2" color="text.secondary">
                    There are no files or folders in this location.
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          
          {/* File preview */}
          {sharedItem.type === 'file' && token && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                borderRadius: theme.shape.borderRadius,
                minHeight: 360,
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <PreviewEngine
                file={{
                  id: sharedItem.id,
                  name: sharedItem.name,
                  size: Number(sharedItem.size) || 0,
                  mimetype: sharedItem.mimeType,
                  mimeType: sharedItem.mimeType,
                }}
                sharePreviewUrlBuilder={(id) =>
                  buildSharePreviewUrl(id, sharePassword)
                }
                onShareDownload={() =>
                  handleFileDownload(sharedItem.id, sharedItem.name)
                }
              />
            </Paper>
          )}
        </>
      )}
    </Container>
    </Box>
  );
};

export default SharedItemViewer; 